import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute } from "@tanstack/react-router";
import { PackageSearchIcon, ServerCrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_COLUMN_VISIBILITY,
  OrderList,
  SELECTABLE_TABLE_DEFAULT_STATE,
} from "~/components/common/orders/order-list";
import { OrdersDisplay } from "~/components/common/orders/orders-display";
import { OrdersFilters } from "~/components/common/orders/orders-filters";
import { APP_SIDEBAR_NAME } from "~/components/layout/sidebars/app-sidebar";
import { SidebarTrigger } from "~/components/ui/sidebar";
import type { Orders_Search_Input } from "~/graphql/graphql.gen";
import { useInfiniteSearchOrdersQuery } from "~/graphql/graphql.gen";
import {
  queryParamsToGraphQLFilters,
  queryParamsToGraphQLSorting,
  queryParamsToTableColumnVisibility,
} from "~/stores/orders-store";
import { searchOrdersQueryParamsSchema } from "~/validators/search-orders-validator";

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/orders/",
)({
  component: RouteComponent,
  validateSearch: (search) => searchOrdersQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <>
      <title>{`${t("app_sidebar.orders")} - ${t("app_name")}`}</title>

      <div className="bg-background flex w-full flex-col md:rounded-md md:border">
        <div className="flex h-full grow flex-col">
          <header className="flex flex-col items-center">
            <div className="flex h-10 w-full items-center gap-2 border-b px-4">
              <SidebarTrigger name={APP_SIDEBAR_NAME} className="-ml-1" />
              <Separator orientation="vertical" className="mr-2" />

              <h2 className="text-sm">{t("app_sidebar.orders")}</h2>
            </div>
            <div className="flex min-h-10 w-full items-center justify-between gap-2 border-b px-4 py-0.75 pl-5.5">
              <div className="flex flex-wrap items-center gap-2">
                <OrdersFilters
                  shortcut
                  filters={search.filters ?? {}}
                  onFiltersChange={(filters) =>
                    void navigate({
                      search: (prev) => ({ ...prev, filters }),
                    })
                  }
                />
              </div>
              <OrdersDisplay />
            </div>
          </header>
          <div className="h-full w-full overflow-auto">
            <Orders />
          </div>
        </div>
      </div>
    </>
  );
}

function Orders() {
  const { t } = useTranslation();
  const search = Route.useSearch();

  const filters = queryParamsToGraphQLFilters(search);

  const query: Orders_Search_Input = {
    filter: filters,
    sort: queryParamsToGraphQLSorting(search),
    pageInfo: {
      first: 50,
    },
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteSearchOrdersQuery(
    {
      input: {
        ...query,
      },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.orders_search.pageInfo.hasNextPage
          ? {
              input: {
                ...query,
                pageInfo: {
                  ...query.pageInfo,
                  after: lastPage.orders_search.pageInfo.endCursor,
                },
              },
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.orders_search),
    },
  );

  const columnVisibility = queryParamsToTableColumnVisibility(
    search,
    DEFAULT_COLUMN_VISIBILITY,
  );

  const orders =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("orders.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (Object.keys(filters).length === 0 && orders.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <PackageSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.empty.title")}</EmptyTitle>
            <EmptyDescription>{t("orders.empty.description")}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <PackageSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("orders.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <OrderList
      data={orders}
      state={{
        ...SELECTABLE_TABLE_DEFAULT_STATE,
        columnVisibility: columnVisibility,
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}
